import {
  Component,
  signal,
  input,
  model,
  computed,
  inject,
  ModelSignal,
  InputSignal,
  Signal
} from '@angular/core';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { STEPS_HOST, type StepIdSignal, type StepsHost } from './steps.contract';

const NOOP_ACTIVE_STEP_ID: StepIdSignal = signal<string | number | undefined>(undefined);

const NOOP_STEPS_HOST: StepsHost = {
  steps: signal<ReadonlyArray<unknown>>([]),
  activeStepId: NOOP_ACTIVE_STEP_ID,
  activeStepIndex: signal(-1),
  disabled: signal(false),
  getStepsCount: () => 0,
  setActiveStepById(): void {}
};

export enum StepType {
  REGULAR = 'regular',
  COMPACT = 'compact'
}

@Component({
  selector: 'td-wb-step',
  templateUrl: 'step.component.html',
  styleUrls: ['step.component.scss'],
  imports: [TranslatePipe],
  host: {
    '[class.td-wb-step--last]': 'isLast()',
    '[class.td-wb-step--complete]': 'isComplete()',
    '[class.td-wb-step--non-linear]': 'nonLinear()',
    '[class.td-wb-step--active]': 'isActive()'
  }
})
export class StepComponent {
  stepId: ModelSignal<string | number | undefined> = model<string | number>();
  disabled: InputSignal<boolean> = input<boolean>(false);
  stepType: InputSignal<StepType> = input<StepType>(StepType.REGULAR);

  private readonly stepsComponent: StepsHost = inject(STEPS_HOST, { optional: true })
    ?? NOOP_STEPS_HOST;

  index: Signal<number> = computed(() => this.stepsComponent.steps().indexOf(this));
  isActive: Signal<boolean> = computed(() => this.stepsComponent.activeStepId() === this.stepId());
  isFirst: Signal<boolean> = computed(() => this.index() === 0);
  isLast: Signal<boolean> = computed(() => this.index() === this.stepsComponent.getStepsCount() - 1);
  position: Signal<number> = computed(() => this.index());

  isComplete: Signal<boolean> = computed(() => {
    const activeIndex: number = this.stepsComponent.activeStepIndex();
    return activeIndex !== -1 && this.index() < activeIndex;
  });

  nonLinear: Signal<boolean> = computed(() => this.stepType() === StepType.COMPACT);

  getStepId(): string | number | undefined {
    return this.stepId();
  }

  setStepId(stepId: string | number): void {
    this.stepId.set(stepId);
  }

  isLinear(): boolean {
    return !this.nonLinear();
  }

  getStepsCount(): number {
    return this.stepsComponent.getStepsCount();
  }

  isDisabled(): boolean {
    return (!this.isComplete() && !this.isActive()) || this.disabled();
  }

  getLabel(): number {
    return this.position() + 1;
  }

  isSelectable(): boolean {
    return !this.isParentDisabled() && this.isComplete() && !this.isDisabled();
  }

  onSelect(): void {
    if (this.isSelectable()) {
      const id = this.getStepId();
      if (id !== undefined) {
        this.stepsComponent.setActiveStepById(id);
      }
    }
  }

  getStepTitleId(): string {
    return `td-wb-step-title-${this.stepId()}`;
  }

  private isParentDisabled(): boolean {
    return this.stepsComponent.disabled();
  }
}
